import type { FinishRunResponse, RunSummaryDto } from '@rw/shared';
import { create } from 'zustand';
import { ApiError, api } from '../lib/api';
import type { LocalRun, RunStore } from './run-store';
import { t } from '../i18n';

export interface SyncStatus {
  syncing: boolean;
  pending: number;
  lastError: string | null;
  lastSyncAt: number | null;
  /** Local runs waiting to be synced (for UI listing). */
  queued: Array<Pick<LocalRun, 'id' | 'startedAt' | 'status' | 'clientDistanceM' | 'error'>>;
}

export const useSync = create<SyncStatus>(() => ({
  syncing: false,
  pending: 0,
  lastError: null,
  lastSyncAt: null,
  queued: [],
}));

const BATCH = 500;

export interface SyncDeps {
  store: RunStore;
  /** Current user — never upload someone else's leftover runs after an account switch. */
  userId: () => string | null;
  isOnline: () => boolean;
  /** Runs currently being recorded (points may still be appended). */
  activeRunId: () => string | null;
}

/**
 * Moves local runs to the server: create → upload points → finish. Every step is
 * idempotent on the server, so retrying after any failure is always safe. Local
 * data is only deleted after the server has accepted and judged the run.
 */
export class SyncManager {
  private running: Promise<void> | null = null;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private backoffMs = 2_000;
  private finishedListeners = new Set<(run: LocalRun, result: FinishRunResponse) => void>();

  constructor(private readonly deps: SyncDeps) {}

  /** Notified each time the server has judged a run. */
  onFinished(fn: (run: LocalRun, result: FinishRunResponse) => void): () => void {
    this.finishedListeners.add(fn);
    return () => this.finishedListeners.delete(fn);
  }

  /** Request a sync pass; coalesces concurrent requests. */
  kick(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = this.loop().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  /** User-requested retry of a run the server refused earlier. */
  async retry(runId: string) {
    const run = await this.deps.store.getRun(runId);
    if (run?.status === 'failed') await this.deps.store.putRun({ ...run, status: 'finished', error: null });
    await this.kick();
  }

  startAuto(intervalMs = 10_000) {
    const tick = () => {
      void this.kick().finally(() => {
        this.timer = setTimeout(tick, useSync.getState().lastError ? this.backoffMs : intervalMs);
      });
    };
    tick();
    const onOnline = () => void this.kick();
    window.addEventListener('online', onOnline);
    return () => {
      if (this.timer) clearTimeout(this.timer);
      window.removeEventListener('online', onOnline);
    };
  }

  private async loop() {
    do {
      this.again = false;
      await this.pass();
    } while (this.again);
  }

  private async pass() {
    const userId = this.deps.userId();
    const all = (await this.deps.store.listRuns()).filter((r) => r.userId === userId);
    const pending = all.filter((r) => r.status !== 'synced');
    this.publish(pending);
    if (!userId || !this.deps.isOnline() || pending.length === 0) return;

    useSync.setState({ syncing: true });
    let error: string | null = null;
    for (const run of pending.sort((a, b) => a.startedAt - b.startedAt)) {
      try {
        await this.syncRun(run);
      } catch (e) {
        const err = e as ApiError;
        if (err instanceof ApiError && !err.isTransient) {
          if (err.status === 401) {
            error = t('Sign in again to sync your runs');
            break;
          }
          // Permanent server refusal: keep the data, mark the run so the user can see why.
          await this.deps.store.putRun({ ...run, status: run.status === 'finished' ? 'failed' : run.status, error: err.message });
          error = err.message;
        } else {
          error = err instanceof ApiError ? err.message : String(e);
          break;
        }
      }
    }
    this.backoffMs = error ? Math.min(this.backoffMs * 2, 60_000) : 2_000;
    const left = (await this.deps.store.listRuns()).filter((r) => r.userId === userId && r.status !== 'synced');
    useSync.setState({ syncing: false, lastError: error, lastSyncAt: error ? useSync.getState().lastSyncAt : Date.now() });
    this.publish(left);
    await this.cleanup();
  }

  private async syncRun(initial: LocalRun) {
    const store = this.deps.store;
    const run = initial;
    // Runs the server refused permanently are only retried on explicit user request.
    if (run.status === 'failed') return;
    let sync = await store.getSync(run.id);

    if (!sync.serverCreated) {
      await api<RunSummaryDto>('/runs', { body: { id: run.id, regionId: run.regionId, startedAt: Math.round(run.startedAt) } });
      sync = { ...sync, serverCreated: true };
      await store.putSync(sync);
    }

    // Upload points in batches beyond what the server already confirmed.
    for (;;) {
      const points = (await store.getPoints(run.id, sync.uploadedSeq)).slice(0, BATCH);
      if (points.length === 0) break;
      await api(`/runs/${run.id}/points`, {
        body: {
          points: points.map(({ runId: _runId, ...p }) => ({ ...p, altitude: p.altitude ?? null, speed: p.speed ?? null })),
        },
        timeoutMs: 30_000,
      });
      sync = { ...sync, uploadedSeq: Math.max(sync.uploadedSeq, points[points.length - 1]!.seq) };
      await store.putSync(sync);
      if (points.length < BATCH) break;
    }

    const latest = (await store.getRun(run.id)) ?? run;
    if ((latest.status !== 'finished' && latest.status !== 'failed') || latest.id === this.deps.activeRunId()) return;

    const pointCount = await store.countPoints(latest.id);
    try {
      const result = await api<FinishRunResponse>(`/runs/${latest.id}/finish`, {
        body: { endedAt: Math.round(latest.endedAt ?? Date.now()), clientDistanceM: latest.clientDistanceM, pointCount },
        timeoutMs: 45_000,
      });
      const done: LocalRun = { ...latest, status: 'synced', result, error: null };
      await store.putRun(done);
      await store.deletePoints(latest.id);
      for (const l of this.finishedListeners) l(done, result);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'POINTS_MISSING') {
        // Server is missing some batches: re-upload everything (idempotent) on the next pass.
        await store.putSync({ ...sync, uploadedSeq: -1 });
        this.again = true;
        return;
      }
      throw e;
    }
  }

  private publish(runs: LocalRun[]) {
    useSync.setState({
      pending: runs.filter((r) => r.status === 'finished' || r.status === 'failed').length,
      queued: runs.map((r) => ({ id: r.id, startedAt: r.startedAt, status: r.status, clientDistanceM: r.clientDistanceM, error: r.error })),
    });
  }

  /** Synced runs are kept briefly (summary screen), then removed from the device. */
  private async cleanup() {
    const cutoff = Date.now() - 3 * 86_400_000;
    for (const r of await this.deps.store.listRuns()) {
      if (r.status === 'synced' && r.updatedAt < cutoff) await this.deps.store.deleteRun(r.id);
    }
  }
}
